import { useMemo } from "react";
import { getLiveAnalyticsResultSummary } from "../../../../lib/sessionRunUtils";
import { analyticsSummaryToCheckResult } from "../../../../models/audit/analyticsTranslator";
import { useCortanaV2Executor } from "../../../../hooks/useCortanaV2Executor";
import { resolveCellError, type CellErrorState } from "../utils/cellError";
import { CheckDecision } from "./CheckDecision";
import type { EvalColumnCellProps } from "./WorkPaperTableV2";

// Analytics cells accept the same props as eval cells; the dedicated name keeps
// the analytics definition from reading as an eval cell.
type AnalyticsColumnCellProps = EvalColumnCellProps;

/**
 * What an analytics cell renders about a failure standing against it. The two
 * analytics-only states below carry their own label as well as their own lead,
 * because the generic "Could Not Generate" misreads both: one cell generated
 * twice, the other holds a verdict it cannot read.
 *
 * `CheckDecision` reads `errorLabel` only where the cell already reports a
 * column-scoped failure, so the label travels with `isColumnFailure` — one
 * without the other renders no label at all.
 */
type AnalyticsCellErrorState = CellErrorState & { errorLabel?: string };

/**
 * More than one verdict stands against the cell, so no single one can be shown.
 * They need not all be readable: one that parsed beside one that did not is
 * still a cell whose live verdict cannot be identified, and badging the readable
 * one would rank them by which survived the parse.
 *
 * Reuses the amber column-failure vocabulary — an analytics column already has a
 * key and a width track, so the box is laid out and the marker costs no geometry
 * — rather than an empty div that would report a two-verdict cell as untested.
 * The collision is named in the console.
 */
const UNRESOLVED_ANALYTICS_VERDICT: AnalyticsCellErrorState = {
  hasError: true,
  isTimeout: false,
  isColumnFailure: true,
  errorLabel: "More Than One Verdict",
  errorLead: "More than one live verdict stands against this cell.",
  errorMessage:
    "Two or more results are live against this column for this sample, and none can be established as the verdict this cell holds, so none is shown. The collision is reported in the browser console.",
};

/**
 * The cell's one verdict arrived in a shape this build could not parse, so it
 * was dropped and the cell holds none.
 *
 * Marked for the same reason the two-verdict state is: an empty box with no
 * click handler reports the cell as untested, and a verdict that exists but
 * cannot be read is a different fact from a sample nobody tested. The cell has
 * nothing to badge — the verdict, its outcome and its exceptions all went with
 * the element — so the marker is all there is to say it.
 *
 * `analyticsVerdictsDroppedForColumns` names the cells this can be said for; a
 * verdict damaged badly enough to lose its column id is not attributable to any
 * cell and stays invisible.
 *
 * Claims only the cell whose single verdict was discarded. A cell that lost two
 * of them is unreadable as well, but the unresolved state above claims it: the
 * remedy this one offers — reload, then re-run — answers a parse this build
 * could not do, not a cell holding results it should never have held.
 */
const DROPPED_ANALYTICS_VERDICT: AnalyticsCellErrorState = {
  hasError: true,
  isTimeout: false,
  isColumnFailure: true,
  errorLabel: "Could Not Read Verdict",
  errorLead: "This cell's verdict could not be read.",
  errorMessage:
    "The result arrived in a shape this version of the workpaper cannot parse. It has not been lost; reload to retry, and re-run the column if it persists.",
};

/**
 * Workpaper-lazy-load per-row cell renderer for analytics columns. Reads the
 * cell's one live verdict from `drySourceRow.analyticsResultsSummary`, so the
 * badge costs no per-row fetch. The heavy verdict — its prose, run manifest and
 * auditor trail — is not read here; `useAnalyticsResultDetail` serves the review
 * pane that shows it.
 */
export const AnalyticsColumnCell = ({
  procedureSessionId,
  drySourceRow,
  column,
  isHighlighted,
  highlightEdges,
  isLast,
  width,
  activeSessionRuns,
  errorRuns,
  successfulRuns,
  currentSelection,
  onCellSelection,
  onToggleLock,
  onRerunPending,
}: AnalyticsColumnCellProps) => {
  const cortanaV2 = useCortanaV2Executor();

  // The verdicts this cell holds that the row's parse could read.
  const parsedVerdictCount = useMemo(() => {
    const analyticsColumnId = column.analyticsColumn?.id;
    if (!analyticsColumnId) return 0;
    return (drySourceRow.analyticsResultsSummary ?? []).filter(
      (r) => r.analyticsColumnId === analyticsColumnId,
    ).length;
  }, [drySourceRow.analyticsResultsSummary, column]);

  // Counted rather than tested for membership: the parse names the column once
  // per verdict it discards, so a column named twice lost two of them.
  const droppedVerdictCount = useMemo(() => {
    const analyticsColumnId = column.analyticsColumn?.id;
    if (!analyticsColumnId) return 0;
    return drySourceRow.analyticsVerdictsDroppedForColumns.filter(
      (droppedForColumn) => droppedForColumn === analyticsColumnId,
    ).length;
  }, [drySourceRow.analyticsVerdictsDroppedForColumns, column]);

  // Both channels count toward the same cell. This surface relies on at most one
  // verdict existing per cell, so a second one of either kind means that no
  // longer holds, and the verdict that happened to parse cannot be shown to be
  // the live one.
  const hasUnresolvedVerdict = parsedVerdictCount + droppedVerdictCount > 1;

  // The cell's single verdict was discarded, so nothing readable reached it.
  // A cell that lost two of them is unreadable too, but there the multiplicity
  // is the fact worth acting on, so the unresolved state claims it and this one
  // means exactly what its copy promises: one verdict, and it could not be read.
  const hasDroppedVerdict =
    parsedVerdictCount === 0 && droppedVerdictCount === 1;

  // Kept beside the translated shape: the translator namespaces its id for the
  // shared selection state, while the lock endpoint and the summary-cache flip
  // need the RAW verdict id.
  //
  // Undefined wherever the multiplicity is unresolvable, so the badge, the lock
  // handler and the prior-result copy all withhold from one value rather than
  // each deciding again.
  const liveSummary = useMemo(() => {
    const analyticsColumnId = column.analyticsColumn?.id;
    if (!analyticsColumnId) return undefined;

    if (hasUnresolvedVerdict) {
      // The report for a cell whose verdicts cannot be resolved, made before
      // `getLiveAnalyticsResultSummary` is consulted because the cell withholds
      // its result either way. It carries both counts: that utility is handed
      // the parsed verdicts alone, so a count taken there understates what
      // arrived by however many the parse discarded. The unresolved tooltip
      // sends the reader to this console.
      console.error(
        `Analytics column ${analyticsColumnId} arrived with ${parsedVerdictCount} readable ` +
          `and ${droppedVerdictCount} unreadable verdicts; at most one is live per cell, ` +
          "so the cell reports none.",
      );
      return undefined;
    }

    return getLiveAnalyticsResultSummary(
      drySourceRow.analyticsResultsSummary,
      analyticsColumnId,
    );
  }, [
    drySourceRow.analyticsResultsSummary,
    column,
    hasUnresolvedVerdict,
    parsedVerdictCount,
    droppedVerdictCount,
  ]);

  const checkResult = useMemo(
    () =>
      liveSummary
        ? analyticsSummaryToCheckResult(liveSummary, column)
        : undefined,
    [liveSummary, column],
  );

  // What puts "Running..." in a cell with no verdict yet: a Generate in flight
  // over this row. Without it an analytics column reads as permanently empty
  // while its workflow runs.
  const isLoading =
    !checkResult?.lockedBy &&
    activeSessionRuns.some(
      (run) =>
        run.sourceRowId === drySourceRow.id &&
        (run.targetColumnIds === null ||
          run.targetColumnIds.includes(column.id)),
    );

  const errorInfo: AnalyticsCellErrorState = hasUnresolvedVerdict
    ? UNRESOLVED_ANALYTICS_VERDICT
    : hasDroppedVerdict
      ? DROPPED_ANALYTICS_VERDICT
      : resolveCellError({
          sourceRowId: drySourceRow.id,
          columnId: column.id,
          resultRunId: checkResult?.runId,
          isLoading,
          errorRuns,
          successfulRuns,
          suppressTimeoutCopy: cortanaV2,
          containedColumnFailure: {
            report: true,
            priorResultVisible: Boolean(liveSummary),
          },
        });

  return (
    <CheckDecision
      currentSelection={currentSelection}
      onCellSelection={onCellSelection}
      checkResult={checkResult}
      sourceRow={drySourceRow}
      column={column}
      width={width}
      isLast={isLast}
      isLoading={isLoading}
      hasError={errorInfo.hasError}
      isTimeout={errorInfo.isTimeout}
      errorMessage={errorInfo.errorMessage}
      isColumnFailure={errorInfo.isColumnFailure}
      priorResultVisible={errorInfo.priorResultVisible}
      errorLead={errorInfo.errorLead}
      errorLabel={errorInfo.errorLabel}
      isHighlighted={isHighlighted}
      highlightEdges={highlightEdges}
      auditSessionId={procedureSessionId}
      columnKind="analytics"
      // The provenance the conclusion affordance derives the verdict method
      // from. Withheld with the badge wherever the cell's verdict is
      // unresolvable, so nothing attributes a verdict this cell declines to
      // show.
      analyticsVerdictProvenance={liveSummary}
      canRerunWithInstructions={false}
      // No lock where the verdict is unresolvable: the toggle names one
      // verdict, and a cell that cannot say which of its verdicts is the
      // answer cannot say which to lock. Naming one anyway would not answer
      // for the cell — this surface relies on a lock being read across every
      // live row the cell holds, so releasing the row that parsed leaves a
      // cell holding two still locked. Such a cell is not unlockable here,
      // and a run that skips it stays skipped until the second row is
      // answered where it came from.
      onToggleLock={
        liveSummary && onToggleLock
          ? () =>
              onToggleLock(
                drySourceRow.id,
                liveSummary.id,
                liveSummary.lockedBy ?? null,
                column.columnVersion,
              )
          : undefined
      }
      onRerunPending={onRerunPending}
    />
  );
};
